/**
 * appVersion (T29 / T29.2) — pure formatting + environment derivation for the
 * build stamp. Every function takes its inputs as parameters (the build object,
 * the API URL) and reads NOTHING from `__APP_BUILD__` or `import.meta.env`
 * internally, so unit tests pass fixtures and surfaces inject the real values.
 *
 * T29.2 reframes the stamp as a SUPPORT IDENTIFIER. The headline is a single
 * version string `1.0.<build>` — `1.0` is the hand-set milestone (major.minor)
 * and `<build>` is the auto-incrementing build number in the patch slot, always
 * a number. Two tiers:
 *   - everyone-view  →  `Web · 1.0.142 · production · ✓ up to date`  (sayable to support)
 *   - detailed grid  →  a 2-column `label | value` grid; value rows like
 *                       `1.0.142 · local · ✓ up to date` + a muted `sha · date`.
 *
 * `channel` (which branch built the app: dev / production / local) is distinct
 * from the server's `env` (which backend the client reached) — a `local` app on
 * the `dev` server reads honestly (D4).
 */

export interface AppBuild {
  semver: string;
  build: string;
  sha: string;
  date: string;
  /** Which branch built the app: `dev` / `production` / `local` (D4). */
  channel: string;
}

export type AppEnv = 'dev' | 'production' | 'local';

/**
 * Derive the deployment environment from the baked API URL (D5). No new env
 * var. `dev` is matched BEFORE `production` because `dev.shopista.ca` contains
 * the `shopista.ca` substring — order matters or every dev build reads as
 * production.
 */
export function deriveEnv(apiBaseUrl: string | undefined): AppEnv {
  const url = apiBaseUrl ?? '';
  if (url.includes('dev.shopista')) return 'dev';
  if (url.includes('shopista.ca')) return 'production';
  return 'local';
}

/**
 * The single version string the support read-out is built on: `1.0.<build>` —
 * hand-set `major.minor` milestone + the build number in the patch slot (D1).
 * The build is always a number (the local fallback is `git rev-list --count`,
 * never the literal `local` — see vite.config.ts), so this never renders
 * `1.0.local`. When the build is somehow non-numeric it still appends honestly
 * rather than guessing.
 */
export function versionString(build: Pick<AppBuild, 'semver' | 'build'>): string {
  return `${majorMinor(build.semver)}.${build.build}`;
}

/**
 * The everyone-view support line (D2): `<platform> · 1.0.<build> · <channel> · <freshness>`.
 * Plainly visible — no gesture — on Settings → About + the login footer; this is
 * the line a user reads to a support engineer. `freshness` is web-only and
 * omitted entirely when absent (native, or a check that couldn't run). Empty
 * parts drop out so the dots never double up.
 */
export function supportLine(
  platform: string,
  build: Pick<AppBuild, 'semver' | 'build'>,
  channel: string,
  freshness: string,
): string {
  const parts = [platform, versionString(build), channel, freshness].filter(
    (p) => p && p.length > 0,
  );
  return parts.join(' · ');
}

/**
 * The headline value cell for an APP component row in the detailed grid:
 * `1.0.<build> · <channel> · <freshness>`. The version + channel + (web-only,
 * may be empty) freshness — the muted `sha · date` is a SEPARATE cell rendered
 * by `metaLine` under it. Empty freshness drops out cleanly.
 */
export function componentValue(
  build: Pick<AppBuild, 'semver' | 'build'>,
  channel: string,
  freshness: string,
): string {
  const parts = [versionString(build), channel, freshness].filter(
    (p) => p && p.length > 0,
  );
  return parts.join(' · ');
}

/**
 * The muted second line under a component row: `<sha> · <shortened date>`. The
 * full SHA and full date — nothing truncated (D3); the grid's value column wraps
 * a long value inside its own column. Empty parts drop out so a build with no
 * date renders just the sha.
 */
export function metaLine(sha: string, date: string): string {
  const parts = [sha, shortDate(date)].filter((p) => p && p.length > 0);
  return parts.join(' · ');
}

/**
 * The headline value cell for the SERVER row (D7): `1.0.<build> · <env>`.
 * Version-led — same column order as `componentValue` (the Mobile/Web rows) and
 * the everyone-view `supportLine`, so version and branch/env line up across rows.
 * When no server version is in hand (`server` is null) the row renders a bare em
 * dash via `serverUnreachable` — this formatter is only called with a real
 * payload.
 */
export function serverValue(
  env: AppEnv,
  server: Pick<AppBuild, 'semver' | 'build'>,
): string {
  return `${versionString(server)} · ${env}`;
}

/** The Server row when the backend couldn't be reached — a quiet em dash (D6). */
export const SERVER_UNREACHABLE = '—';

/**
 * Shorten a build date for display. A full ISO datetime
 * (`2026-06-08T22:29:34-03:00`) overflows a narrow phone row, so collapse it to
 * `YYYY-MM-DD HH:MM` by STRING-SLICING — never `new Date()`, which would shift
 * the commit's wall-clock into the viewer's timezone. Take the date part before
 * the `T`, a space, then the first five characters of the time (`HH:MM`),
 * dropping seconds and the offset. A date-only string (`2026-06-06`) or an empty
 * string has no `T` and passes through unchanged.
 */
export function shortDate(date: string): string {
  const tIndex = date.indexOf('T');
  if (tIndex === -1) return date;
  const datePart = date.slice(0, tIndex);
  const timePart = date.slice(tIndex + 1, tIndex + 1 + 5);
  return `${datePart} ${timePart}`;
}

/**
 * "Am I on the latest build?" — the web-only freshness answer (D6). Three states:
 *   - latest unknown (no `/version.json` in hand — local dev, native, offline)
 *     → '' (omitted entirely; the support line just drops the segment).
 *   - reachable + current  → `✓ up to date`.
 *   - reachable + a newer build deployed → `⟳ Update available`.
 * Unlike T29's verbose label, the build number to update onto is NOT named here
 * — the everyone-view stays sayable; the developer reads the build from the grid.
 */
export function freshnessLabel(isStale: boolean, latestBuild: string | null): string {
  if (latestBuild === null) return '';
  if (!isStale) return '✓ up to date';
  return '⟳ Update available';
}

/**
 * Reduce a semver to major.minor for display. `1.0.0` → `1.0`; a label already
 * at `1.0` passes through. Falls back to the raw input if it has fewer than two
 * dotted segments.
 */
export function majorMinor(semver: string): string {
  const segments = semver.split('.');
  const major = segments[0];
  const minor = segments[1];
  if (major !== undefined && minor !== undefined) return `${major}.${minor}`;
  return semver;
}
